// src/types/entitlements.ts
var MODULE_CAP_RANK = {
  NONE: 0,
  READ: 1,
  FULL: 2
};
var LICENSED_MODULES = [
  "analytics",
  "clients",
  "forms",
  "links",
  "organization",
  "reports",
  "tasks",
  "team",
  "workflows"
];

// src/types/support.ts
var LANE_SUPPORT_TYPES = {
  ACCOUNT: [
    "ACCOUNT_ADD_PERSON",
    "ACCOUNT_ROLE_CHANGE",
    "ACCOUNT_SIGN_IN_ACCESS",
    "ACCOUNT_CASELOAD",
    "ACCOUNT_OTHER"
  ],
  PLATFORM: [
    "PLATFORM_BUG",
    "PLATFORM_QUESTION",
    "PLATFORM_FEATURE_REQUEST",
    "PLATFORM_BILLING",
    "PLATFORM_DATA_WRONG",
    "PLATFORM_OTHER"
  ]
};
var SUPPORT_TYPE_LABEL = {
  ACCOUNT_ADD_PERSON: "Add a person",
  ACCOUNT_ROLE_CHANGE: "Change a role or permission",
  ACCOUNT_SIGN_IN_ACCESS: "Can't sign in / access",
  ACCOUNT_CASELOAD: "Client or caseload assignment",
  ACCOUNT_OTHER: "Something else",
  PLATFORM_BUG: "Something's broken (bug)",
  PLATFORM_QUESTION: "How do I\u2026 (question)",
  PLATFORM_FEATURE_REQUEST: "Feature request",
  PLATFORM_BILLING: "Billing or plan",
  PLATFORM_DATA_WRONG: "Data looks wrong",
  PLATFORM_OTHER: "Something else"
};
function laneForSupportType(type) {
  return type.startsWith("ACCOUNT_") ? "ACCOUNT" : "PLATFORM";
}
function defaultSupportTypeForLane(lane) {
  return lane === "ACCOUNT" ? "ACCOUNT_OTHER" : "PLATFORM_OTHER";
}
function formatSupportReference(reference) {
  return `SR-${reference}`;
}

// src/types/index.ts
var PERMISSIONS = {
  // ── Personal (Main) ──
  ORG_DASHBOARD: "org:dashboard",
  TASKS_MY_TASKS: "tasks:my_tasks",
  // ── Organization (coarse; base key = Own scope, `:all` = All scope) ──
  TEAM_MANAGE: "team:manage",
  TEAM_MANAGE_ALL: "team:manage:all",
  TEAM_MANAGE_ACTIONS: "team:manage:actions",
  CLIENTS_MANAGE: "clients:manage",
  CLIENTS_MANAGE_ALL: "clients:manage:all",
  CLIENTS_MANAGE_ACTIONS: "clients:manage:actions",
  LOCATIONS_MANAGE: "locations:manage",
  LOCATIONS_MANAGE_ALL: "locations:manage:all",
  ROLE_EXTEND: "role:extend",
  ASSIGNMENTS_MANAGE: "assignments:manage",
  ORG_SETTINGS_MANAGE: "org:settings:manage",
  // ── Workflows (coarse: execute / manage; manage includes execute) ──
  RED_DOT_EXECUTE: "red_dot:execute",
  RED_DOT_MANAGE: "red_dot:manage",
  /**
   * Change who OWNS a Red Dot (ADO 564). Deliberately separate from
   * `red_dot:manage`, which BCBAs and Clinical Directors also hold — ownership
   * transfer is an org-admin action, so it needs a key only Org Admin carries.
   */
  RED_DOT_TRANSFER_OWNER: "red_dot:transfer_owner",
  ACTION_CENTER_FULL: "action_center:full",
  PREFERENCE_EXECUTE: "preference:execute",
  PREFERENCE_MANAGE: "preference:manage",
  // ── Insights ──
  RED_DOT_ANALYTICS: "red_dot:analytics",
  // ── System area (Super Admin) ──
  ROLES_MANAGE: "roles:manage",
  PERMISSIONS_MANAGE: "permissions:manage",
  PLATFORM_DASHBOARD_MANAGE: "platform_dashboard:manage",
  ORGANIZATIONS_MANAGE: "organizations:manage",
  USER_LOOKUP_MANAGE: "user_lookup:manage",
  SUBSCRIPTIONS_MANAGE: "subscriptions:manage",
  INVOICES_MANAGE: "invoices:manage",
  EMAIL_LOGS_MANAGE: "email_logs:manage",
  FILE_ASSETS_MANAGE: "file_assets:manage",
  PLANS_MANAGE: "plans:manage",
  REQUESTS_MANAGE: "requests:manage",
  // ── Support & Help Center ──
  SUPPORT_REQUESTS_MANAGE: "support_requests:manage",
  /** Reopen a RESOLVED request. Deliberately separate from :manage — reopening
   * reverses a decision a handler already communicated to the requester, so it
   * is granted rather than implied (ADO #542 item 17). */
  SUPPORT_REQUESTS_REOPEN: "support_requests:reopen",
  // ── Settings (every role) ──
  PROFILE_MANAGE: "profile:manage",
  // ── Legacy task-admin keys (Task Scheduler module — super-admin-only for now) ──
  TASK_READ: "task:read",
  TASK_ADMIN: "task:admin"
};
var RED_DOT_DISPOSITIONS = [
  "TRANSFER_TO_NEW",
  "KEEP_WITH_OUTGOING",
  "UNASSIGN"
];

// src/constants/password.ts
var PASSWORD_POLICY = {
  minLength: 12,
  maxLength: 128,
  regex: /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{12,}$/,
  message: "Password must be at least 12 characters and include an uppercase letter, a number, and a special character."
};

// src/constants/preference-assessment.ts
var PA_RESOURCE_LINKS = {
  freeOperantVideo: {
    label: "Free operant observation \u2014 video",
    url: "https://www.youtube.com/watch?v=GA_6-zmAQbA"
  },
  singleStimulusVideo: {
    label: "Single stimulus \u2014 video",
    url: "https://www.youtube.com/watch?v=ClZh0kCKfgw"
  },
  pairedVideo: {
    label: "Paired stimulus \u2014 video",
    url: "http://youtube.com/watch?v=CnBraS9rmz4"
  },
  mswoInstructions: {
    label: "MSWO \u2014 detailed instructions",
    url: "https://ebip.ledfordlab.org/multiple-stimulus-without-replacement/"
  },
  mswoTangiblesVideo: {
    label: "MSWO with tangibles \u2014 video",
    url: "https://www.youtube.com/watch?v=fEEelCgBkWA"
  },
  mswoEdiblesVideo: {
    label: "MSWO with edibles \u2014 video",
    url: "https://www.youtube.com/watch?v=PQtb_5FvU1A"
  }
};
var PA_PATH_RESOURCES = {
  free_operant: [PA_RESOURCE_LINKS.freeOperantVideo],
  free_operant_or_single: [
    PA_RESOURCE_LINKS.freeOperantVideo,
    PA_RESOURCE_LINKS.singleStimulusVideo
  ],
  paired_stimulus: [PA_RESOURCE_LINKS.pairedVideo],
  mswo_plus_msw: [PA_RESOURCE_LINKS.mswoInstructions, PA_RESOURCE_LINKS.mswoEdiblesVideo],
  mswo: [
    PA_RESOURCE_LINKS.mswoInstructions,
    PA_RESOURCE_LINKS.mswoTangiblesVideo,
    PA_RESOURCE_LINKS.mswoEdiblesVideo
  ]
};

// src/constants/public-email-providers.ts
import disposable from "disposable-email-domains";
import free from "free-email-domains";

// src/constants/isp-personal-domains.json
var isp_personal_domains_default = {
  domains: [
    "comcast.net",
    "sbcglobal.net",
    "verizon.net",
    "att.net",
    "bellsouth.net",
    "earthlink.net",
    "cox.net",
    "charter.net",
    "rogers.com",
    "shaw.ca",
    "telus.net",
    "btinternet.com",
    "virginmedia.com",
    "sky.com",
    "t-online.de",
    "freenet.de",
    "gmx.de",
    "orange.fr",
    "wanadoo.fr",
    "free.fr"
  ]
};

// src/constants/public-email-providers.ts
var DISPOSABLE = new Set(disposable);
var FREE = new Set(free);
var ISP = new Set(isp_personal_domains_default.domains);
function classifyDomain(domain) {
  const d = domain.trim().toLowerCase();
  if (!d) return "unknown";
  if (DISPOSABLE.has(d)) return "disposable";
  if (ISP.has(d)) return "isp";
  if (FREE.has(d)) return "free";
  return "unknown";
}
var PUBLIC_EMAIL_PROVIDERS = {
  classifyDomain,
  has: (domain) => classifyDomain(domain) !== "unknown"
};

// src/constants/red-dot.ts
var RED_DOT_QUESTION_SET_VERSION = "2.0";
var OPT1_QUESTIONS_V1 = [
  { id: "opt1_pre_q1", text: "Does more than one tech work with this client?" },
  { id: "opt1_q1", text: "Is progress consistent across all BTs/RBTs?" },
  { id: "opt1_q2", text: "Is there high variability in the data path?" },
  { id: "opt1_q3", text: "Is the client highly motivated during teaching sessions?" },
  {
    id: "opt1_q4",
    text: "Looking at the last 5 data points, does each data point show that the predetermined number of required trials have been implemented?"
  },
  {
    id: "opt1_q5",
    text: "Has the skill acquisition goal been implemented at least 4 times out of the past 5 sessions?"
  },
  {
    id: "opt1_q6",
    text: "Does the client display any challenging behaviors during the skill acquisition program?"
  },
  { id: "opt1_q6a", text: "Please list the challenging behavior(s)" }
];
var OPT1_QUESTIONS_V2 = [
  { id: "opt1_pre_q1", text: "Is this client supported by more than one tech?" },
  {
    id: "opt1_q1",
    text: "Does the client's progress look similar no matter which team member is working with them?"
  },
  {
    id: "opt1_q2",
    text: "Does the client's data path show a lot of variability from session to session?"
  },
  {
    id: "opt1_q3",
    text: "During teaching sessions, does the client tend to be highly motivated to participate in the red dotted goal?"
  },
  {
    id: "opt1_q4",
    text: "Looking at the last 5 data points, was the planned number of trials completed in each session?"
  },
  {
    id: "opt1_q5",
    text: "Out of the last 5 sessions the client was in attendance, was this skill acquisition goal run in at least 4 of them?"
  },
  { id: "opt1_q6", text: "During the red dotted goal, does the client show any challenging behaviors?" },
  { id: "opt1_q6a", text: "Please list the challenging behavior(s)" }
];
function redDotOpt1Questions(schemaVersion) {
  return schemaVersion === "2.0" ? OPT1_QUESTIONS_V2 : OPT1_QUESTIONS_V1;
}
var RED_DOT_OPT1_TEXT = Object.fromEntries(
  redDotOpt1Questions(RED_DOT_QUESTION_SET_VERSION).map((q) => [q.id, q.text])
);
var RED_DOT_ACK_COPY_VERSION = 1;
var RED_DOT_ACK_COPY = {
  title: "Welcome to Red Dot",
  intro: [
    "Thank you for supporting your clients and our organization by answering these questions honestly.",
    "Before you get started, a few things we want you to know:"
  ],
  points: [
    {
      heading: "You are your client's best advocate.",
      text: "Your answers help us understand what each client needs. Your responses for the red dotted goal will help us pinpoint the reason this goal is not progressing."
    },
    {
      heading: "Your responses are not a reflection of you as a technician.",
      text: "When a response shows that something isn't working, it simply tells us where we need to make a systems change, and your answers are what allow us to do that."
    },
    {
      heading: "Accuracy matters.",
      text: "The more accurately you respond, the better the clinical decisions we can make together."
    }
  ]
};

// src/constants/rbac.ts
var WORKFLOW_PERMISSION_PRESETS = {
  red_dot: {
    execute: ["red_dot:execute"],
    manage: ["red_dot:execute", "red_dot:manage"]
  },
  preference_assessment: {
    execute: ["preference:execute"],
    manage: ["preference:execute", "preference:manage"]
  }
};
var WORKFLOW_ROUTES = {
  red_dot: "/red-dot",
  preference_assessment: "/preference-assessment"
};
var WORKFLOW_LEVEL_RANK = {
  none: 0,
  execute: 1,
  manage: 2
};
function toSet(keys) {
  return keys instanceof Set ? keys : new Set(keys);
}
function keysForLevel(code, level) {
  const preset = WORKFLOW_PERMISSION_PRESETS[code];
  if (!preset || level === "none") return [];
  return level === "manage" ? [...preset.manage] : [...preset.execute];
}
function allWorkflowKeys(code) {
  const preset = WORKFLOW_PERMISSION_PRESETS[code];
  if (!preset) return [];
  return [.../* @__PURE__ */ new Set([...preset.execute, ...preset.manage])];
}
function levelForWorkflow(roleKeys, code) {
  const preset = WORKFLOW_PERMISSION_PRESETS[code];
  if (!preset) return "none";
  const keys = toSet(roleKeys);
  const hasAll = (arr) => arr.length > 0 && arr.every((k) => keys.has(k));
  if (hasAll(preset.manage)) return "manage";
  if (hasAll(preset.execute)) return "execute";
  const anyKey = /* @__PURE__ */ new Set([...preset.execute, ...preset.manage]);
  for (const k of keys) if (anyKey.has(k)) return "execute";
  return "none";
}
var DATA_SCOPE_OPTIONS = ["none", "own", "all"];
var SCOPED_RESOURCES = {
  team: { base: "team:manage", all: "team:manage:all" },
  clients: { base: "clients:manage", all: "clients:manage:all" },
  locations: { base: "locations:manage", all: "locations:manage:all" }
};
var SCOPED_RESOURCE_ALL_KEY = {
  team: SCOPED_RESOURCES.team.all,
  clients: SCOPED_RESOURCES.clients.all,
  locations: SCOPED_RESOURCES.locations.all
};
function scopeForResource(roleKeys, r) {
  const keys = toSet(roleKeys);
  if (keys.has(SCOPED_RESOURCES[r].all)) return "all";
  if (keys.has(SCOPED_RESOURCES[r].base)) return "own";
  return "none";
}
function keysForScope(r, scope) {
  const { base, all } = SCOPED_RESOURCES[r];
  if (scope === "none") return [];
  return scope === "all" ? [base, all] : [base];
}
var SYSTEM_PERMISSION_KEYS = /* @__PURE__ */ new Set([
  "roles:manage",
  "permissions:manage",
  "platform_dashboard:manage",
  "organizations:manage",
  "requests:manage",
  "user_lookup:manage",
  "subscriptions:manage",
  "invoices:manage",
  "email_logs:manage",
  "file_assets:manage",
  "plans:manage"
]);
function sectionForPermission(p) {
  const key = p.key;
  if (key === "red_dot:analytics") return "insights";
  if (key.startsWith("red_dot:") || key.startsWith("preference:") || key.startsWith("preference_assessment:") || key.startsWith("workflow:")) {
    return "workflows";
  }
  if (key.startsWith("analytics:") || key.startsWith("report:")) return "insights";
  if (SYSTEM_PERMISSION_KEYS.has(key)) return "system";
  if (key === "profile:manage" || key === "org:settings:manage") return "settings";
  if (/^(team|clients|locations|role|assignments|user|group|location|client|supervision):/.test(key)) {
    return "organization";
  }
  return "organization";
}

// src/constants/sla.ts
var DEFAULT_RED_DOT_SLA_DAYS = 14;
var DEFAULT_SLA_GREEN_THRESHOLD = 50;
var DEFAULT_SLA_YELLOW_THRESHOLD = 20;

// src/constants/index.ts
var SUPER_ADMIN_ROLE_NAME = "Super Admin";
var SESSION_STATUS = {
  DRAFT: "DRAFT",
  IN_PROGRESS: "IN_PROGRESS",
  PENDING_REVIEW: "PENDING_REVIEW",
  APPROVED: "APPROVED",
  RETURNED: "RETURNED",
  SUBMITTED: "SUBMITTED",
  LOCKED: "LOCKED"
};
var ROLES = {
  ORG_ADMIN: "ORG_ADMIN",
  BCBA: "BCBA",
  RBT: "RBT"
};
var QUESTION_TYPES = {
  TEXT: "text",
  CHOICE: "choice",
  MULTI_CHOICE: "multi_choice",
  NUMBER: "number",
  SCALE: "scale"
};
var AUTO_SAVE_INTERVAL_MS = 5e3;
var ACCESS_LEVELS = {
  READ: "READ",
  FULL: "FULL"
};
var ACCESS_LEVEL_RANK = {
  READ: 1,
  FULL: 2
};
var PERMISSION_MODULES = {
  TEAM: "team",
  FORMS: "forms",
  WORKFLOWS: "workflows",
  REPORTS: "reports",
  ANALYTICS: "analytics",
  ORGANIZATION: "organization",
  LINKS: "links",
  TASKS: "tasks"
};
var TASK_STATUSES = {
  PENDING: "PENDING",
  BLOCKED: "BLOCKED",
  AVAILABLE: "AVAILABLE",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  SKIPPED: "SKIPPED",
  OVERDUE: "OVERDUE"
};
var TASK_TYPES = {
  CLINICAL_FORM: "CLINICAL_FORM",
  REVIEW: "REVIEW",
  MEETING: "MEETING",
  AD_HOC: "AD_HOC"
};
var ORG_REGISTRATION_REQUEST_STATUSES = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED"
};
var ORG_SIZES = {
  SOLO: "SOLO",
  SMALL: "SMALL",
  MEDIUM: "MEDIUM",
  LARGE: "LARGE",
  ENTERPRISE: "ENTERPRISE"
};
var ORG_SIZE_LABELS = {
  SOLO: "Solo",
  SMALL: "Small (2-10)",
  MEDIUM: "Medium (11-50)",
  LARGE: "Large (51-200)",
  ENTERPRISE: "Enterprise (200+)"
};
var ORGANIZATION_STATUSES = {
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  CLOSED: "CLOSED"
};
var SUBSCRIPTION_STATUSES = {
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  PAST_DUE: "PAST_DUE",
  CANCELED: "CANCELED",
  EXPIRED: "EXPIRED"
};
var INVOICE_STATUSES = {
  DRAFT: "DRAFT",
  ISSUED: "ISSUED",
  PAID: "PAID",
  VOID: "VOID"
};
var API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh",
    MFA_VERIFY: "/auth/mfa/verify"
  },
  ORGANIZATIONS: {
    BASE: "/organizations",
    MEMBERS: "/organizations/:orgId/members",
    INVITATIONS: "/organizations/:orgId/invitations"
  },
  FORMS: {
    DEFINITIONS: "/forms/definitions",
    SESSIONS: "/forms/sessions"
  },
  LINKS: {
    BASE: "/links",
    BY_ID: "/links/:id",
    DEACTIVATE: "/links/:id/deactivate",
    SUBMISSIONS: "/links/:id/submissions"
  },
  PUBLIC: {
    SURVEY: "/public/survey/:token",
    SURVEY_SESSIONS: "/public/survey/:token/sessions",
    RESULTS: "/public/results/:sessionId",
    FIND_MY_RESULTS: "/public/find-my-results"
  },
  GROUPS: {
    BASE: "/groups",
    BY_ID: "/groups/:id",
    MEMBERS: "/groups/:id/members",
    WORKFLOWS: "/groups/:id/workflows",
    CLIENTS: "/groups/:id/clients",
    CLIENT_BY_ID: "/groups/:id/clients/:clientId"
  },
  PLANS: {
    BASE: "/admin/plans",
    BY_ID: (id) => `/admin/plans/${id}`,
    ASSIGN: (orgId) => `/admin/plans/organizations/${orgId}/assign`
  }
};

// src/utils/index.ts
function generateToken(length = 32) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function formatDate(date) {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString().split("T")[0];
}
function isDefined(value) {
  return value !== null && value !== void 0;
}
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}
function humanizeStatus(value) {
  if (!value) return "";
  return value.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
}
export {
  ACCESS_LEVELS,
  ACCESS_LEVEL_RANK,
  API_ENDPOINTS,
  AUTO_SAVE_INTERVAL_MS,
  DATA_SCOPE_OPTIONS,
  DEFAULT_RED_DOT_SLA_DAYS,
  DEFAULT_SLA_GREEN_THRESHOLD,
  DEFAULT_SLA_YELLOW_THRESHOLD,
  INVOICE_STATUSES,
  LANE_SUPPORT_TYPES,
  LICENSED_MODULES,
  MODULE_CAP_RANK,
  ORGANIZATION_STATUSES,
  ORG_REGISTRATION_REQUEST_STATUSES,
  ORG_SIZES,
  ORG_SIZE_LABELS,
  PASSWORD_POLICY,
  PA_PATH_RESOURCES,
  PA_RESOURCE_LINKS,
  PERMISSIONS,
  PERMISSION_MODULES,
  PUBLIC_EMAIL_PROVIDERS,
  QUESTION_TYPES,
  RED_DOT_ACK_COPY,
  RED_DOT_ACK_COPY_VERSION,
  RED_DOT_DISPOSITIONS,
  RED_DOT_OPT1_TEXT,
  RED_DOT_QUESTION_SET_VERSION,
  ROLES,
  SCOPED_RESOURCES,
  SCOPED_RESOURCE_ALL_KEY,
  SESSION_STATUS,
  SUBSCRIPTION_STATUSES,
  SUPER_ADMIN_ROLE_NAME,
  SUPPORT_TYPE_LABEL,
  TASK_STATUSES,
  TASK_TYPES,
  WORKFLOW_LEVEL_RANK,
  WORKFLOW_PERMISSION_PRESETS,
  WORKFLOW_ROUTES,
  allWorkflowKeys,
  classifyDomain,
  deepClone,
  defaultSupportTypeForLane,
  formatDate,
  formatSupportReference,
  generateToken,
  humanizeStatus,
  isDefined,
  keysForLevel,
  keysForScope,
  laneForSupportType,
  levelForWorkflow,
  redDotOpt1Questions,
  scopeForResource,
  sectionForPermission,
  slugify
};
//# sourceMappingURL=index.mjs.map